import { createContext, useContext, useRef, useState } from "react";
import { Auth } from "aws-amplify";

import { KLAVIYO_ENDPOINT } from "data/constants";
import { useAuth } from "./AuthHook";

const SUBSCRIPTION_TO_KLAVIYO_NEWSLETTER_TO_BE_DONE =
  "SUBSCRIPTION_TO_KLAVIYO_NEWSLETTER_TO_BE_DONE";

const KlaviyoContext = createContext();

const useKlaviyo = () => {
  const context = useContext(KlaviyoContext);
  if (!context) {
    throw new Error(
      "`useKlaviyo` must be used within `KlaviyoContextProvider`"
    );
  }
  return context;
};

const KlaviyoContextProvider = ({ children }) => {
  const { authenticatedUser } = useAuth();

  const isFirstRun = useRef(true); // used to limit checkIsSubscribed call in MenuContainer
  const [isSubscribed, setIsSubscribed] = useState(false);

  // Simplify localStorage key management by directly using user email
  const getLocalStorageKey = (type, email) => `fp_klaviyo_${type}:${email}`;

  const checkIsSubscribed = async () => {
    if (!authenticatedUser) return false;
    const cognitoIdToken = (await Auth.currentSession())
      .getIdToken()
      .getJwtToken();
    try {
      const response = await fetch(KLAVIYO_ENDPOINT, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "X-Cognito-Id-Token": cognitoIdToken,
          "x-project-origin": "feelperformer",
        },
      });
      const json = await response.json();
      return json.message.includes("YES");
    } catch (error) {
      console.error("Error checking subscription status:", error);
      return false;
    }
  };

  const changeSubscriptionStatus = async (action) => {
    if (!authenticatedUser) return;
    const cognitoIdToken = (await Auth.currentSession())
      .getIdToken()
      .getJwtToken();
    try {
      await fetch(KLAVIYO_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Cognito-Id-Token": cognitoIdToken,
          "x-project-origin": "feelperformer",
        },
        body: JSON.stringify({
          action,
        }),
      });
    } catch (error) {
      console.error("Error subscribing user:", error);
    }
  };

  const hasDenied = () => {
    if (!authenticatedUser) return false;
    const deniedKey = getLocalStorageKey("denied");
    return !!localStorage.getItem(deniedKey);
  };

  const setDenied = () => {
    if (!authenticatedUser) return;
    const deniedKey = getLocalStorageKey("denied");
    localStorage.setItem(deniedKey, "1");
  };

  const hasAccepted = () => {
    return !!localStorage.getItem(
      SUBSCRIPTION_TO_KLAVIYO_NEWSLETTER_TO_BE_DONE
    );
  };

  const setAccepted = () => {
    localStorage.setItem(SUBSCRIPTION_TO_KLAVIYO_NEWSLETTER_TO_BE_DONE, "1");
  };

  const removeAcceptedKey = () => {
    localStorage.removeItem(SUBSCRIPTION_TO_KLAVIYO_NEWSLETTER_TO_BE_DONE);
  };

  return (
    <KlaviyoContext.Provider
      value={{
        subscribe: async () => await changeSubscriptionStatus("subscribe"),
        unsubscribe: async () => await changeSubscriptionStatus("unsubscribe"),
        checkIsSubscribed,
        hasDenied,
        setDenied,
        hasAccepted,
        setAccepted,
        removeAcceptedKey,
        isFirstRun,
        isSubscribed,
        setIsSubscribed,
      }}
    >
      {children}
    </KlaviyoContext.Provider>
  );
};

export { KlaviyoContextProvider, useKlaviyo };
